import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { Pencil } from 'react-bootstrap-icons';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS, ROLE_ADMIN, ROLE_SUPER_ADMIN, ROLE_SALES, ROLE_SALES_MANAGER, ROLE_SALES_AND_INVENTORY_MANAGER } from '../../helpers/constants';
import { userHasAnyRole } from '../../helpers/roleHelpers';

export const NAME_SAVED_EVENT = 'mhc-customer-name-saved';
export default function CustomerNameEdit({ record, entity = 'contact', onSaved, className = '' }) {
  const [open, setOpen] = useState(false);
  const allowed = userHasAnyRole(ROLE_ADMIN, ROLE_SUPER_ADMIN, ROLE_SALES, ROLE_SALES_MANAGER, ROLE_SALES_AND_INVENTORY_MANAGER);
  const id = record?._id;
  useEffect(() => setOpen(false), [id]);
  if (!allowed || !id) return null;
  return <span className="customer-name-edit" onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}>
    <Button type="button" variant="link" size="sm" className={'px-2 ' + className} aria-label="Edit customer name" title="Edit customer and co-buyer names" onClick={() => setOpen(true)}><Pencil size={16}/></Button>
    {open && <NameDialog key={entity + id} id={id} entity={entity} onClose={() => setOpen(false)} onSaved={updated => {
      setOpen(false);
      if (onSaved) onSaved(updated);
      window.dispatchEvent(new CustomEvent(NAME_SAVED_EVENT, { detail: { entity, record: updated } }));
    }}/>}
  </span>;
}

function NameDialog({ id, entity, onClose, onSaved }) {
  const [initial, setInitial] = useState(null);
  const [values, setValues] = useState({ buyer: '', coBuyer: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const busy = useRef(false), active = useRef(true);
  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    // Compact cards may omit the co-buyer. Read the stored values before allowing a save.
    makeApiRequests({ endpoint: entity === 'contract' ? ENDPOINTS.CONTRACTS_SEARCH_NEW : ENDPOINTS.CONTACTS_SEARCH_NEW,
      method: 'POST', requestBody: { filter: { _id: id }, populate: false, pageSize: 1 }, signal: controller.signal }).then(result => {
      if (cancelled) return;
      const found = result.response?.results?.find(row => row._id === id);
      if (result.error || !found) { setError(result.error || 'Customer not available. Close and try again.'); return; }
      const names = { buyer: found.buyer || '', coBuyer: found.coBuyer || '' };
      setInitial(names); setValues(names);
    });
    return () => { cancelled = true; active.current = false; controller.abort(); };
  }, [id, entity]);
  async function save(event) {
    event.preventDefault();
    if (!initial || busy.current) return;
    if (!values.buyer.trim()) { setError('Customer name is required.'); return; }
    busy.current = true; setSaving(true); setError('');
    const result = await makeApiRequests({ endpoint: entity === 'contract' ? ENDPOINTS.CONTRACTS_WITH_ID(id) : ENDPOINTS.CONTACTS_WITH_ID(id),
      method: 'PUT', requestBody: { buyer: values.buyer.trim(), coBuyer: values.coBuyer.trim(), _expectedNames: initial } });
    if (!active.current) return;
    busy.current = false; setSaving(false);
    if (result.error || result.response?._id !== id) { setError(result.error || 'Save was not confirmed. Close and reopen the name editor to check.'); return; }
    onSaved(result.response);
  }
  return <Modal show onHide={() => { if (!busy.current) onClose(); }} centered backdrop="static" aria-labelledby="customer-name-title">
    <Modal.Header closeButton={!saving}><Modal.Title id="customer-name-title">Edit customer name</Modal.Title></Modal.Header>
    <Form onSubmit={save}>
      <Modal.Body>
        {error && <Alert variant="danger" role="alert">{error}</Alert>}
        {!initial && !error && <p role="status">Loading names…</p>}
        <fieldset disabled={!initial || saving}>
          <Form.Group controlId="name-buyer"><Form.Label>Customer name</Form.Label><Form.Control autoFocus value={values.buyer} maxLength={250} required onChange={event => setValues({ ...values, buyer: event.target.value })}/></Form.Group>
          <Form.Group controlId="name-coBuyer"><Form.Label>Co-buyer name (optional)</Form.Label><Form.Control value={values.coBuyer} maxLength={250} onChange={event => setValues({ ...values, coBuyer: event.target.value })}/></Form.Group>
        </fieldset>
      </Modal.Body>
      <Modal.Footer><Button type="button" variant="outline-secondary" disabled={saving} onClick={onClose}>Cancel</Button><Button type="submit" disabled={!initial || saving}>{saving ? 'Saving…' : 'Save'}</Button></Modal.Footer>
    </Form>
  </Modal>;
}
